import { clsx } from 'clsx';
import { DataTable, type DataTableColumn } from '../../_shared/DataTable';
import { useCampaignAds } from '../hooks/useCampaignAds';
import { CURRENCY, COMPACT_NUMBER, NUMBER, PERCENT, RATIO_X } from '../../_shared/format';
import type { PerformanceCampaignAdRow } from '@/services/api';

const FREQ_BADGE: Record<string, string> = {
  healthy: 'bg-emerald-100 text-emerald-700',
  consider_refresh: 'bg-amber-100 text-amber-700',
  critical_refresh: 'bg-rose-100 text-rose-700',
};

export function CampaignAdsTab({ campaignId }: { campaignId: string }) {
  const { data, loading } = useCampaignAds(campaignId, true);

  const columns: DataTableColumn<PerformanceCampaignAdRow>[] = [
    {
      key: 'ad_name',
      label: 'Ad',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-medium text-navy-900" title={row.ad_name}>
            {row.ad_name}
          </span>
          {row.media_type ? (
            <span className="text-[10px] text-slate-400">{row.media_type}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'spend',
      label: 'Spend',
      align: 'right',
      width: '110px',
      render: (row) => CURRENCY.format(row.spend),
    },
    {
      key: 'impressions',
      label: 'Impr.',
      align: 'right',
      width: '90px',
      render: (row) => COMPACT_NUMBER.format(row.impressions),
    },
    {
      key: 'ctr',
      label: 'CTR',
      align: 'right',
      width: '70px',
      render: (row) => PERCENT(row.ctr),
    },
    {
      key: 'purchases',
      label: 'Purch.',
      align: 'right',
      width: '70px',
      render: (row) => NUMBER.format(row.purchases),
    },
    {
      key: 'roas',
      label: 'ROAS',
      align: 'right',
      width: '70px',
      render: (row) => RATIO_X(row.roas),
    },
    {
      key: 'cpa',
      label: 'CPA',
      align: 'right',
      width: '90px',
      render: (row) => CURRENCY.format(row.cpa),
    },
    {
      key: 'frequency_status',
      label: 'Fatigue',
      width: '120px',
      render: (row) =>
        row.frequency_status ? (
          <span
            className={clsx(
              'rounded px-1.5 py-0.5 text-[10px] font-mono',
              FREQ_BADGE[row.frequency_status] ?? 'bg-slate-100 text-slate-600',
            )}
          >
            {row.frequency_status.replace('_', ' ')}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      key: 'is_early_signal_ad',
      label: 'Signal',
      width: '70px',
      align: 'center',
      render: (row) =>
        row.is_early_signal_ad ? (
          <span className="rounded bg-teal-100 px-1.5 py-0.5 text-[10px] font-mono text-teal-700">
            EARLY
          </span>
        ) : null,
    },
  ];

  return (
    <DataTable<PerformanceCampaignAdRow>
      columns={columns}
      rows={data?.rows ?? []}
      rowKey={(r) => r.ad_id}
      loading={loading}
      emptyText="No ads found for this campaign."
    />
  );
}
